/**
 * Blinkit-theme account screen (``/profile``). Wallet / coins / roles / console switching come from
 * useProfileController (the classic page's logic). Layout: profile card, quick tiles, grouped lists
 * ("Your information", "Other information"), console shortcuts for staff roles, and log out.
 */
import { Link } from "react-router-dom";
import {
    Bell, BadgePercent, Calendar, ChevronRight, Coins, FileText, Headphones, LogOut, MapPin, Pencil, Receipt, Share2,
    ShieldCheck, User, Wallet,
} from "lucide-react";
import { poweredByBrand } from "../../../lib/utils";
import { t } from "../../../lib/i18n";
import { fmtInr } from "../../../lib/priceBreakdown";
import LangSwitch from "../../../components/LangSwitch";
import NotificationPermissionCard from "../../../components/NotificationPermissionCard";
import { BkEmptyState, BkPageHeader } from "../../../components/blinkit/BkUI";
import { CONSOLE_BY_ROLE, useProfileController } from "../controllers/useProfileController";
import { lseOn } from "../../../lib/lse";

function Tile({ to, icon: Icon, label, value, testid }) {
    return (
        <Link to={to} data-testid={testid} className="bg-card rounded-2xl border border-border/80 p-2.5 sm:p-3 flex flex-col items-start gap-2 min-w-0 active:scale-[0.98] transition hover:border-foreground/20">
            <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon size={18} /></span>
            <span className="min-w-0 w-full">
                <span className="block text-[13px] font-bold truncate">{label}</span>
                {value != null && <span className="block text-[12px] text-muted-foreground truncate tabular-nums">{value}</span>}
            </span>
        </Link>
    );
}

function Row({ to, icon: Icon, label, sub, testid, onClick }) {
    const inner = (
        <>
            <span className="w-9 h-9 shrink-0 rounded-xl bg-muted text-foreground/70 flex items-center justify-center"><Icon size={18} /></span>
            <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold truncate">{label}</span>
                {sub && <span className="block text-[12px] text-muted-foreground truncate">{sub}</span>}
            </span>
            <ChevronRight size={17} className="text-muted-foreground shrink-0" />
        </>
    );
    const cls = "w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition";
    return onClick
        ? <button type="button" onClick={onClick} data-testid={testid} className={cls}>{inner}</button>
        : <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
}

function Group({ title, children, testid }) {
    return (
        <section data-testid={testid}>
            <h2 className="px-1 mb-2 text-[12px] font-extrabold uppercase tracking-[0.12em] text-muted-foreground">{title}</h2>
            <div className="bg-card rounded-2xl border border-border/80 divide-y divide-border overflow-hidden">{children}</div>
        </section>
    );
}

export default function BkAccountView() {
    const {
        user, logout, tenant, lang, nav, wallet, coinsValue, activeConsole, otherRoles, switchTo,
    } = useProfileController();

    if (!user) {
        return (
            <div className="pb-32" data-testid="bk-account-page">
                <BkPageHeader title={t("profile", lang)} back="/" right={<LangSwitch compact />} />
                <BkEmptyState icon={User} testid="bk-account-signed-out" title={t("login_title", lang)}
                              body="Log in to see your orders, wallet, saved addresses and offers."
                              action={<Link to="/login" data-testid="login-cta" className="inline-flex h-11 items-center px-7 rounded-xl bg-primary text-primary-foreground text-[14px] font-bold">Log in / Sign up</Link>} />
            </div>
        );
    }

    const initial = (user.first_name?.[0] || user.mobile_number?.[0] || "U").toUpperCase();
    return (
        <div className="pb-32 min-w-0" data-testid="bk-account-page">
            <BkPageHeader title={t("profile", lang)} back="/" right={<LangSwitch compact />} />

            {/* iter178 — desktop (lg+): account card + shortcuts | the menus, instead of one 768px column in the middle of the
                page. Below lg the two wrappers are plain blocks inside the same ``space-y-5`` flow, so nothing changes. */}
            <div className="px-4 pt-4 space-y-5 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] lg:gap-5 lg:items-start">
                <div className="space-y-5 min-w-0">
                <section className="bg-card rounded-2xl border border-border/80 p-4 flex items-center gap-4" data-testid="bk-account-card">
                    <span className="w-14 h-14 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[22px] font-extrabold">{initial}</span>
                    <div className="min-w-0 flex-1">
                        <p className="text-[18px] font-extrabold leading-tight line-clamp-2 break-words" data-testid="profile-name">{user.first_name || "Customer"}</p>
                        <p className="text-[13px] text-muted-foreground mt-0.5 tabular-nums">+91 {user.mobile_number}</p>
                    </div>
                    <Link to="/profile/edit" data-testid="profile-edit-cta" aria-label="Edit profile"
                          className="shrink-0 h-9 px-3 rounded-lg border border-border text-[12.5px] font-bold inline-flex items-center gap-1.5 hover:bg-muted"><Pencil size={13} /> Edit</Link>
                </section>

                <div className="grid grid-cols-4 gap-2 sm:gap-2.5" data-testid="bk-account-tiles">
                    <Tile to="/orders" icon={Receipt} label="Orders" testid="menu-orders" />
                    <Tile to="/wallet" icon={Wallet} label={t("wallet", lang)} value={wallet ? fmtInr(wallet.balance || 0) : null} testid="profile-wallet-balance" />
                    <Tile to="/coins" icon={Coins} label="Coins" value={String(coinsValue ?? 0)} testid="profile-coins-balance" />
                    <Tile to="/support" icon={Headphones} label="Help" testid="menu-support-tile" />
                </div>

                <NotificationPermissionCard />

                {(activeConsole || otherRoles.length > 0) && (
                    <Group title="Your consoles" testid="my-consoles-card">
                        {activeConsole && (
                            <Row to={activeConsole.to} icon={activeConsole.Icon} label={activeConsole.label} sub={`Open your dashboard for ${tenant?.name || "this store"}`} testid={`active-console-open-${user.role}`} />
                        )}
                        {otherRoles.map((r) => {
                            const cfg = CONSOLE_BY_ROLE[r.role];
                            return <Row key={`${r.role}-${r.user_id}`} icon={cfg.Icon} label={cfg.label} sub={r.tenant_name || r.tenant_slug || "Platform"} onClick={() => switchTo(r)} testid={`console-open-${r.role}`} />;
                        })}
                    </Group>
                )}

                </div>
                <div className="space-y-5 min-w-0">
                <Group title="Your information" testid="bk-account-info">
                    <Row to="/addresses" icon={MapPin} label="Saved addresses" testid="menu-addresses" />
                    <Row to="/orders?tab=bookings" icon={Calendar} label="My bookings" testid="menu-bookings" />
                    <Row to="/store-coupons" icon={BadgePercent} label={t("coupons", lang)} sub="Offers and in-store coupons" testid="menu-coupons" />
                    <Row to="/refer" icon={Share2} label="Refer a shop · Earn" sub="Share your code, earn on every order" testid="menu-refer" />
                    <Row to="/notifications" icon={Bell} label="Notifications" testid="menu-notifications" />
                    {lseOn(tenant, "subscriptions") && <Row to="/baskets" icon={Calendar} label="My baskets" sub="Weekly / daily groceries in one tap" testid="menu-baskets" />}
                </Group>

                <Group title="Other information" testid="bk-account-other">
                    <Row to="/support" icon={Headphones} label="Help & support" testid="menu-support" />
                    <Row to="/legal/terms" icon={FileText} label="Terms & conditions" testid="menu-terms" />
                    <Row to="/legal/privacy" icon={ShieldCheck} label="Privacy policy" testid="menu-privacy" />
                </Group>

                <button type="button" data-testid="logout-btn" onClick={async () => { try { await logout(); } finally { nav("/"); } }}
                        className="w-full h-12 rounded-2xl border border-destructive/30 bg-card text-destructive text-[14px] font-bold inline-flex items-center justify-center gap-2 hover:bg-destructive/5">
                    <LogOut size={17} /> {t("logout", lang)}
                </button>

                </div>

                <div className="pt-2 pb-4 text-center lg:col-span-2" data-testid="tenant-footer">
                    <p className="text-[13px] font-semibold text-foreground/80">{tenant?.name}</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">Powered by {poweredByBrand(tenant?.branding?.powered_by_text)}</p>
                </div>
            </div>
        </div>
    );
}
